import getAgent from '@egjs/agent';
import { NAVER_APP_WATCH_OS_NATIVE_AGENT, NAVER_APP_WEAR_OS_NATIVE_AGENT, PAY_APP_WATCH_OS_NATIVE_AGENT, PAY_APP_WEAR_OS_NATIVE_AGENT, XWhaleAgentRegEx, NaverRealEstateAppRegEx, OldInternetExplorerEx, NewInternetExplorerEx, appUserAgentRegEx, nativeAppUserAgentRegEx } from '../common/constant.mjs';
import { compareAPPVersion } from './version.mjs';

function getAppVersion(userAgent) {
  const appRegex = new RegExp(appUserAgentRegEx);
  const nativeRegex = new RegExp(nativeAppUserAgentRegEx);
  const appMatchingData = appRegex.exec(userAgent);
  const nativeAppMatchingData = nativeRegex.exec(userAgent);
  if (appMatchingData) {
    return {
      isPayApp: appMatchingData[1] === 'pay',
      isNaverApp: appMatchingData[1] === 'search',
      isMobile: true,
      serviceCode: appMatchingData[2],
      appVersion: appMatchingData[3],
      isShoppingApp: ['navershopping', 'navershoppingbrowser'].includes(appMatchingData[1]),
      isShoppingAppNative: ['navershopping'].includes(appMatchingData[1]),
      isShoppingAppInAppBrowser: ['navershoppingbrowser'].includes(appMatchingData[1])
    };
  }
  if (nativeAppMatchingData) {
    return {
      isPayApp: nativeAppMatchingData[4] === 'NaverPay',
      isNaverApp: ['NaverSearch', 'CrosswalkSearch'].includes(nativeAppMatchingData[4]),
      isMobile: true,
      serviceCode: '0',
      appVersion: nativeAppMatchingData[5],
      isShoppingApp: ['navershopping'].includes(nativeAppMatchingData[4]),
      isShoppingAppNative: ['navershopping'].includes(nativeAppMatchingData[4]),
      isShoppingAppInAppBrowser: false
    };
  }
  return null;
}

/**
 * @param {string} userAgent - UserAgent 문자열
 * @returns {Object} - UserAgent 문자열을 기반으로 확인할 수 있는 기기 정보 및 버전 정보를 리턴합니다.
 */
function generateUserAgentValues(userAgent) {
  const ua = getAgent(userAgent);
  const osName = ua.os.name.toLowerCase();
  const osVersion = ua.os.version;
  const browserName = ua.browser.name.toLowerCase();
  const browserVersion = ua.browser.version;
  const appResult = getAppVersion(userAgent);
  const isPayApp = Boolean(appResult?.isPayApp);
  const isNaverApp = Boolean(appResult?.isNaverApp);
  const isShoppingApp = Boolean(appResult?.isShoppingApp);
  const isShoppingAppNative = Boolean(appResult?.isShoppingAppNative);
  const isShoppingAppInAppBrowser = Boolean(appResult?.isShoppingAppInAppBrowser);
  const isMobileOnly = Boolean(appResult?.isMobile) || ua.isMobile;
  const isMobile = isMobileOnly;
  const isTablet = isMobileOnly;
  const isNaverAppModal = userAgent.endsWith('NaverModal');
  const isNaverOrPayApp = isNaverApp || isPayApp;
  const isWebApp = !isNaverOrPayApp;
  const isNaverWatchOS = NAVER_APP_WATCH_OS_NATIVE_AGENT.test(userAgent) || NAVER_APP_WEAR_OS_NATIVE_AGENT.test(userAgent);
  const isPayWatchOS = PAY_APP_WATCH_OS_NATIVE_AGENT.test(userAgent) || PAY_APP_WEAR_OS_NATIVE_AGENT.test(userAgent);
  const isWatchOS = PAY_APP_WATCH_OS_NATIVE_AGENT.test(userAgent) || NAVER_APP_WATCH_OS_NATIVE_AGENT.test(userAgent);
  const isWearOS = PAY_APP_WEAR_OS_NATIVE_AGENT.test(userAgent) || NAVER_APP_WEAR_OS_NATIVE_AGENT.test(userAgent);
  const isXWhaleWebView = XWhaleAgentRegEx.test(userAgent);
  const isNaverRealEstateApp = NaverRealEstateAppRegEx.test(userAgent);
  const serviceCode = appResult?.serviceCode || '0';
  const appVersion = appResult?.appVersion || '0';
  const isNaverOM = userAgent.includes('NaverOM');
  const isAndroid = osName === 'android';
  const isIOS = osName === 'ios';
  const isWindows = osName === 'window';
  const isMac = osName === 'mac';
  const isBrowser = !isMobile;
  const isDesktop = !isMobile;
  const checkAvailableNaverApp = targetAppVersion => isNaverApp && compareAPPVersion(targetAppVersion, '<=', appVersion);
  const checkAvailablePayApp = targetAppVersion => isPayApp && compareAPPVersion(targetAppVersion, '<=', appVersion);
  const checkAvailableApp = (targetNaverAppVersion, targetPayAppVersion) => checkAvailableNaverApp(targetNaverAppVersion) || checkAvailablePayApp(targetPayAppVersion);
  const isInternetExplorer = OldInternetExplorerEx.test(userAgent) || NewInternetExplorerEx.test(userAgent);
  const isSamsungBrowser = browserName.includes('samsung');
  const isSafari = ua.browser.webkit;
  const isChrome = ua.browser.chromium;
  return {
    isPayApp,
    isMobile,
    isNaverApp,
    isShoppingApp,
    isShoppingAppNative,
    isShoppingAppInAppBrowser,
    isNaverAppModal,
    isNaverOrPayApp,
    isWebApp,
    isNaverWatchOS,
    isPayWatchOS,
    isWatchOS,
    isWearOS,
    isXWhaleWebView,
    isNaverRealEstateApp,
    serviceCode,
    appVersion,
    isNaverOM,
    /**
     * @deprecated isMobile과 값이 같습니다. 다음 메이저 버전업 때 삭제될 예정입니다.
     */
    isMobileOnly,
    /**
     * @deprecated 더이상 userAgent로 타블렛을 완벽하게 구분할 수 없습니다. 다음 메이저 버전업 때 삭제될 예정입니다.
     */
    isTablet,
    isAndroid,
    isIOS,
    checkAvailableNaverApp,
    checkAvailablePayApp,
    checkAvailableApp,
    isInternetExplorer,
    isSamsungBrowser,
    isSafari,
    isChrome,
    isWindows,
    /**
     * @deprecated !isMobile, isDesktop과 값이 같습니다. 다음 메이저 버전업 때 삭제될 예정입니다.
     */
    isBrowser,
    isDesktop,
    isMac,
    /**
     * @deprecated agentInfo를 사용해주세요. 다음 메이저 버전업 때 삭제될 예정입니다.
     */
    ua,
    agentInfo: ua,
    osVersion,
    browserName,
    browserVersion,
    browserInfo: ua.browser
  };
}

export { generateUserAgentValues };
